import React, { useState, useEffect } from 'react';
import {
  Users, Search, Plus, Shield, CheckCircle, Ban,
  Edit, Trash2, UserPlus, Crown, User, X, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import ConfirmDialog from '../shared/ConfirmDialog';

const roleColors = {
  OWNER: 'bg-purple-100 text-purple-700',
  HOSPITAL_ADMIN: 'bg-blue-100 text-blue-700',
  STAFF: 'bg-gray-100 text-gray-700',
};
const roleIcons = { OWNER: Crown, HOSPITAL_ADMIN: Shield, STAFF: User };

const emptyForm = { name: '', email: '', password: '', role: 'STAFF', organization: '', department: '' };
const emptyEditForm = { name: '', role: '', organization: '', department: '' };

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadUsers = () => {
    setLoading(true);
    api.get('/api/admin/users')
      .then(setUsers)
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadUsers(); }, []);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error('Name, email, and password are required');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/admin/users', form);
      toast.success('User created successfully');
      setShowCreate(false);
      setForm(emptyForm);
      loadUsers();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (user) => {
    setEditForm({
      name: user.name || '',
      role: user.role || 'STAFF',
      organization: user.organization || '',
      department: user.department || '',
    });
    setEditingUser(user);
    setShowEdit(true);
  };

  const handleUpdate = async () => {
    if (!editForm.name) {
      toast.error('Name is required');
      return;
    }
    setSubmitting(true);
    try {
      // Update user details
      await api.put(`/api/admin/users/${editingUser.id}`, {
        name: editForm.name,
        organization: editForm.organization || null,
        department: editForm.department || null,
      });
      // If role changed, update role separately
      if (editForm.role !== editingUser.role) {
        await api.put(`/api/admin/users/${editingUser.id}/role?role=${editForm.role}`);
      }
      toast.success('User updated successfully');
      setShowEdit(false);
      setEditingUser(null);
      loadUsers();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (userId) => {
    try {
      await api.put(`/api/admin/users/${userId}/deactivate`);
      toast.success('User deactivated');
      loadUsers();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleActivate = async (userId) => {
    try {
      await api.put(`/api/admin/users/${userId}/activate`);
      toast.success('User activated');
      loadUsers();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/api/admin/users/${deleteTarget.id}`);
      toast.success('User permanently deleted');
      setDeleteTarget(null);
      loadUsers();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeleting(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const activeCount = users.filter(u => u.is_active).length;
  const inactiveCount = users.filter(u => !u.is_active).length;

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-500 mt-1">Manage users across all facilities</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setShowCreate(true); }} className="btn-primary gap-2" data-testid="add-user-btn">
          <UserPlus className="w-4 h-4" />
          Add User
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-gray-500 mb-2">
            <Users className="w-4 h-4" />
            <span className="text-sm">Total Users</span>
          </div>
          <p className="text-2xl font-bold text-gray-900" data-testid="total-users-count">{users.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Active</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-red-600 mb-2">
            <Ban className="w-4 h-4" />
            <span className="text-sm">Inactive</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{inactiveCount}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-2 text-purple-600 mb-2">
            <Crown className="w-4 h-4" />
            <span className="text-sm">Admins</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{users.filter(u => u.role === 'OWNER' || u.role === 'HOSPITAL_ADMIN').length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input placeholder="Search users..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-users-input" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'OWNER', 'HOSPITAL_ADMIN', 'STAFF'].map((role) => (
            <button
              key={role}
              onClick={() => setRoleFilter(role)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                roleFilter === role ? 'bg-[var(--niko-teal)] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {role === 'all' ? 'All Roles' : role.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" /></div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">User</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Role</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Organization</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user) => {
                  const RoleIcon = roleIcons[user.role] || User;
                  return (
                    <tr key={user.id} className="hover:bg-gray-50 group" data-testid="user-row">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--niko-teal)] to-[var(--niko-green)] flex items-center justify-center text-white font-semibold">
                            {user.name?.charAt(0) || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${roleColors[user.role] || 'bg-gray-100'}`}>
                          <RoleIcon className="w-3 h-3" />
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{user.organization || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                          user.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEdit(user)}
                            className="p-2 text-gray-400 hover:text-[var(--niko-teal)] hover:bg-[var(--niko-teal)]/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            title="Edit User"
                            data-testid="edit-user-btn"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          {user.is_active ? (
                            <button
                              onClick={() => handleDeactivate(user.id)}
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                              title="Deactivate"
                              data-testid="deactivate-user-btn"
                            >
                              <Ban className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivate(user.id)}
                              className="p-2 text-gray-400 hover:text-green-500 hover:bg-green-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                              title="Activate"
                              data-testid="activate-user-btn"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => setDeleteTarget(user)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            title="Delete User"
                            data-testid="delete-user-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreate(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg" data-testid="create-user-modal">
            <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Add User</h2>
              <button onClick={() => setShowCreate(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><Label>Full Name *</Label><Input className="mt-1" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="user-name-input" /></div>
              <div><Label>Email *</Label><Input type="email" className="mt-1" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="user-email-input" /></div>
              <div><Label>Password *</Label><Input type="password" className="mt-1" value={form.password} onChange={e => setForm({...form, password: e.target.value})} data-testid="user-password-input" /></div>
              <div>
                <Label>Role</Label>
                <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                  <option value="STAFF">Staff</option>
                  <option value="HOSPITAL_ADMIN">Hospital Admin</option>
                  <option value="OWNER">Owner</option>
                </select>
              </div>
              <div><Label>Organization</Label><Input className="mt-1" value={form.organization} onChange={e => setForm({...form, organization: e.target.value})} /></div>
              <div><Label>Department</Label><Input className="mt-1" value={form.department} onChange={e => setForm({...form, department: e.target.value})} /></div>
            </div>
            <div className="border-t border-gray-100 px-6 py-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleCreate} disabled={submitting} className="flex-1 btn-primary" data-testid="submit-user-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create User
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowEdit(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg" data-testid="edit-user-modal">
            <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Edit User</h2>
              <button onClick={() => setShowEdit(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--niko-teal)] to-[var(--niko-green)] flex items-center justify-center text-white font-semibold">
                  {editingUser.name?.charAt(0) || '?'}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{editingUser.email}</p>
                  <p className="text-xs text-gray-500">Joined {new Date(editingUser.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div><Label>Full Name *</Label><Input className="mt-1" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="edit-user-name-input" /></div>
              <div>
                <Label>Role</Label>
                <select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={editForm.role} onChange={e => setEditForm({...editForm, role: e.target.value})} data-testid="edit-user-role-select">
                  <option value="STAFF">Staff</option>
                  <option value="HOSPITAL_ADMIN">Hospital Admin</option>
                  <option value="OWNER">Owner</option>
                </select>
              </div>
              <div><Label>Organization</Label><Input className="mt-1" value={editForm.organization} onChange={e => setEditForm({...editForm, organization: e.target.value})} data-testid="edit-user-org-input" /></div>
              <div><Label>Department</Label><Input className="mt-1" value={editForm.department} onChange={e => setEditForm({...editForm, department: e.target.value})} data-testid="edit-user-dept-input" /></div>
            </div>
            <div className="border-t border-gray-100 px-6 py-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowEdit(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleUpdate} disabled={submitting} className="flex-1 btn-primary" data-testid="submit-edit-user-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Update User
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Delete User"
        message={`Are you sure you want to permanently delete "${deleteTarget?.name}" (${deleteTarget?.email})? This action cannot be undone.`}
      />
    </div>
  );
};

export default UsersPage;
